import { MoreHorizontal } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * One 44px control and the short menu it opens, for a *page header* action that does not fit a
 * phone. Below `md` only.
 *
 * The sibling of `RailPanel`, and separate from it for three reasons rather than styling taste:
 * that one is chalk on the charcoal rail while this sits on the paper ground; that one is
 * positioned `fixed` off the header's own `h-14`, which is correct only because the app header
 * never scrolls; and `<main>` *does* scroll, so a page-header menu has to be `absolute` inside a
 * `relative` wrapper or it would stay behind while its own button scrolled away.
 *
 * What the two must keep identical is the **layering**, not the surface: the menu is `z-40` over a
 * `z-30` click-catcher, so `Modal`'s `z-50` still lands above whichever menu raised it.
 *
 * `children` is a render prop taking `close` for the same reason RailPanel's is — a row dismisses
 * the menu before its handler runs, so anything the handler raises is read over the screen rather
 * than through the menu.
 */
export function OverflowMenu({
  label,
  children,
}: {
  /** Names the control and the menu to a screen reader. */
  label: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);

  useEffect(() => {
    if (open) menuRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="relative md:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
        className="flex size-11 items-center justify-center rounded-lg border border-ink-800/15 text-ink-800 transition hover:bg-white"
      >
        <MoreHorizontal className="size-4" aria-hidden />
      </button>

      {open ? (
        <>
          {/* Untinted: a backdrop dark enough to see would read as a modal over a two-item menu. */}
          <div className="fixed inset-0 z-30" onClick={close} aria-hidden />
          <div
            ref={menuRef}
            tabIndex={-1}
            role="menu"
            aria-label={label}
            className="absolute right-0 top-full z-40 mt-2 w-[min(15rem,calc(100vw-4rem))] overflow-hidden rounded-lg border border-ink-800/15 bg-white py-1 shadow-lg outline-none"
          >
            {children(close)}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** A row inside an `OverflowMenu`. Full-width so the whole strip is the 44px tap target. */
export const overflowMenuItem =
  'flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-ink-800 hover:bg-felt-50';
